<template>
  <div class="gallery">
    <div v-if="medias && medias.length" class="overlay" :class="[loading && 'reloading']">
      <div class="loader large"></div>
    </div>
    <div class="activeImage">
      <div ref="swiper" class="swiper-container swiper-container-horizontal" :class="{ reloading: loading, 'has-pinch': isTouchDevice }">
        <div ref="swiperWrapper" class="swiper-wrapper" :style="{ height: galleryHeightPx }">
          <template v-for="(media, index) of medias">
            <div
              v-if="media.type === 'IMAGE'"
              :id="`image-id-${index}`"
              :key="media.url"
              class="swiper-slide"
              :alt="media.alt || media.product.title"
              :type="media.type"
              :style="{ height: galleryHeightPx }"
            >
              <PreserveRatio :width="widthRatio" :height="heightRatio">
                <div class="pinch-zoom" data-zoom-initialize="false" :data-zoom-img="media.zoomUrl.replace('{width}', zoomWidth)">
                  <ResponsiveImage 
                  :url="media.url" 
                  :lazy="index !== 0" 
                  :alt="altTextPrimary"
                  />
                </div>
              </PreserveRatio>
            </div>
            <div
              v-else-if="media.type === 'VIDEO' && mounted"
              :id="`image-id-${index}`"
              :key="media.url"
              class="swiper-slide"
              :alt="media.alt || media.product.title"
              :type="media.type"
            >
              <div>
                <video-player
                  v-model="videosPlaying[index]"
                  :active="currentIndex === index"
                  :poster="media.thumbnailUrl"
                  :hls="media.hlsUrl"
                  :dash="media.dashUrl"
                  :mp4="media.url"
                  :product="media.product"
                  @play="onVideoPlay($event, index)"
                  @pause="onVideoPause($event, index)"
                ></video-player>
              </div>
            </div>
          </template>
        </div>
        <!-- Add Pagination -->
        <div v-show="paginationShow" ref="pagination" class="swiper-pagination-mobile"></div>
      </div>

      <div class="swiper-pagination-desktop">
        <ul class="thumbnails">
          <template v-for="(media, index) of medias">
            <template v-if="media.type === 'IMAGE'">
              <li
                :key="media.url"
                class="item"
                :class="currentIndex === index && 'active'"
                @mouseover="onThumbnailHover($event, index)"
                @click.passive="onThumbnailClick($event, index)"
              >
                <a class="thumbnail-link">
                  <ResponsiveImage 
                    :url="media.url" 
                    :widths="[64]" 
                    :breakpoints="[]"
                    :alt="altTextGallery"
                  /> 
                </a>
              </li>
            </template>
            <template v-else-if="media.type === 'VIDEO'">
              <li
                :key="media.url"
                class="item video-play"
                :type="media.type"
                :class="currentIndex === index && 'active'"
                @mouseover="onThumbnailHover($event, index)"
                @click.passive="onVideoThumbnailClick($event, index)"
              >
                <a class="thumbnail-link video-thumbnail">
                  <PlaySmallSvg v-if="!isVideoPlaying" class="icon-thumbnail-play" />
                  <PauseSmallSvg v-if="isVideoPlaying" class="icon-thumbnail-pause" />
                </a>
              </li>
            </template>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import PinchZoom from 'pinch-zoom-js';
import ImportUtils from '@/components/commons/ImportUtils.js';
import ClickZoom from '@/components/commons/ClickZoom.js';
import PreserveRatio from '@/components/commons/PreserveRatio.vue';
import PlaySmallSvg from '@/assets/video/play-small-new.svg';
import PauseSmallSvg from '@/assets/video/pause-small-new.svg';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';

export default {
  name: 'Gallery',
  components: {
    ResponsiveImage,
    'video-player': defineAsyncComponent(() => import(/* webpackChunkName: "video" */ './Video.vue')),
    PlaySmallSvg,
    PauseSmallSvg,
    PreserveRatio,
  },
  props: {
    medias: {
      type: Array,
      required: true,
      default: []
    },
    loading: {
      type: Boolean,
      default: false,
    },
    widthRatio: {
      type: Number,
      default: 2890,
    },
    heightRatio: {
      type: Number,
      default: 1500,
    },
  },
  data() {
    return {
      swiper: null,
      currentIndex: 0,
      isVideoPlaying: false,
      videosPlaying: {},
      galleryHeight: 'auto',
      paginationShow: true,
      mounted: false,
      zoomWidth: 0,
      isTouchDevice: false,
    };
  },
  computed: {
    galleryHeightPx() {
      return this.galleryHeight === 'auto' ? 'auto' : `${this.galleryHeight}px`;
    },
    altTextPrimary(){
      return `${(this.medias[this.currentIndex]?.alt || this.medias[this.currentIndex]?.product.title) ?? 'Product'} - Primary View ${this.currentIndex + 1} of ${this.medias.length} (Click To Zoom).`;
    },
    altTextGallery(){
      return `${(this.medias[this.currentIndex]?.alt || this.medias[this.currentIndex]?.product.title) ?? 'Product'} - Gallery View ${this.currentIndex + 1} of ${this.medias.length}.`;
    }
  },
  watch: {
    medias: {
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue && this.mounted) {
          this.$nextTick(() => {
            this.onThumbnailHover(null, 0);
            this.swiper?.update();
            this.swiper?.loopDestroy();
            this.swiper?.loopCreate();
            this.initZoom();
          });
        }
      },
    },

    loading: {
      handler(newValue, oldValue) {
        if (newValue && this.$refs && this.$refs.swiperWrapper) {
          this.galleryHeight = this.$refs.swiperWrapper.offsetHeight;
        } else {
          setTimeout(() => {
            this.galleryHeight = 'auto';
          }, 500);
        }
      },
    },
  },
  mounted() {
    this.mounted = true;

    this.isTouchDevice = 'ontouchstart' in document.documentElement;
    if (this.isTouchDevice) {
      this.onFocus();
    } else {
      this.$el.addEventListener('mouseenter', this.onFocus, {
        capture: true,
        once: true,
      });
      this.$el.addEventListener('focus', this.onFocus, {
        capture: true,
        once: true,
      });
    }
  },
  methods: {
    onFocus() {
      try {
        this.setUpSwiper();
      } catch (e) {
        console.error(e);
      }
    },

    async setUpSwiper() {
      const isTouchDevice = 'ontouchstart' in document.documentElement;
      const Swiper = await ImportUtils.getSwiper();
      this.swiper = new Swiper(this.$refs.swiper, {
        zoom: false,
        loop: true,
        effect: 'slide',
        speed: isTouchDevice ? 300 : 80,
        preloadImages: false,
        keyboard: true,
        threshold: 30,
        noSwipingClass: 'article-video-bottom-bar, .article-video-volume',
        pagination: {
          el: this.$refs.pagination,
          clickable: !isTouchDevice,
          type: 'bullets',
        },
        lazy: {
          loadPrevNext: true,
          loadOnTransitionStart: isTouchDevice,
          loadPrevNextAmount: 1,
        },
      });

      this.swiper.on('slideChangeTransitionEnd', () => {
        this.currentIndex = parseInt(this.getSlideIndex(this.swiper), 10);
        this.emitSlide();
      });

      this.initZoom();
    },
    initZoom() {
      if (!this.$el) {
        return;
      }

      const pinchZoom = this.$el.querySelector('.pinch-zoom');
      if (pinchZoom) {
        const ZOOM_SIZE = 2342;
        this.zoomWidth = ZOOM_SIZE * Math.max(1, Math.floor(window.devicePixelRatio));
        const isTouchDevice = 'ontouchstart' in document.documentElement;
        const pinchZooms = this.$el.querySelectorAll('.pinch-zoom');
        [...pinchZooms].forEach((dom) => {
          dom.addEventListener('touchend', (event) => {
            event.cancelable && event.preventDefault();
          });

          isTouchDevice &&
            new PinchZoom(dom, {
              draggableUnzoomed: false,
              minZoom: 1,
            });

          dom.addEventListener(
            'click',
            (e) => {
              if (dom.dataset.zoomInitialize === 'false') {
                dom.dataset.zoomInitialize = true;
                const url = dom.dataset.zoomImg;

                const clickZoom = new ClickZoom(dom, {
                  url,
                  callback: () => {
                    clickZoom.onClick({
                      target: clickZoom.target,
                      pageX: e.pageX,
                      pageY: e.pageY,
                    });
                    this.$emit('trackEvents','Old_Gallery_Zoom_Clicked');
                  },
                });
              }
            },
            {
              capture: false,
              passive: true,
            },
          );
        });
       }
    },
    getSlideIndex(swipe) {
      if (!swipe || !swipe.slides) {
        return 0;
      }
      let { activeIndex } = swipe;
      const slidesLen = swipe && swipe.slides ? swipe.slides.length : 0;
      if (swipe && swipe.params && swipe.params.loop) {
        switch (swipe.activeIndex) {
          case 0:
            activeIndex = slidesLen - 3;
            break;
          case slidesLen - 1:
            activeIndex = 0;
            break;
          default:
            --activeIndex;
        }
      }

      this.paginationShow = swipe.slides[activeIndex + 1] ? !swipe.slides[activeIndex + 1].querySelector('.article-video-container') : false;

      return activeIndex;
    },
    slideTo(value) {
      window.setTimeout(() => {
        // $this.nextTick does not work
        if (this.swiper && !this.isVideoPlaying) {
          this.swiper.slideTo(value + 1);
        }
      }, 0);
    },
    onThumbnailHover(event, value) {
      if (this.swiper && !this.isVideoPlaying) {
        this.swiper.slideTo(value + 1, 0);
      }
    },
    onThumbnailClick(event, value) {
      this.$emit('trackEvents','Old_Gallery_Thumbnail_Clicked');
      if (this.swiper) {
        this.swiper.slideTo(value + 1, 0);
      }
    },
    onVideoPlay(event, index) {
      this.videosPlaying[index] = true;
      this.isVideoPlaying = true;
      this.$emit('videoplay');
    },
    onVideoPause(event, index) {
      this.videosPlaying[index] = false;
      this.isVideoPlaying = false;
      this.$emit('videopause');
    },
    onVideoThumbnailClick(event, index) {
      this.videosPlaying[index] = !this.videosPlaying[index];
    },
    emitSlide() {
      const media = this.medias[this.currentIndex];
      if (media) {
        this.$emit('changeSlide', { type: media.type, index: this.currentIndex });
      }
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
/* purgecss start ignore */
@import 'swiper/dist/css/swiper.min.css';
/* purgecss end ignore */

.gallery {
  position: relative;
  margin-bottom: 10px;

  .overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: z-index 100ms linear;

    &.reloading {
      z-index: 40;
    }
  }

  .activeImage {
    margin: 0px -20px 0px;
    overflow: hidden;
    position: relative;

    .pinch-zoom-container {
      min-height: 160px;
    }

    .swiper-container {
      overflow: hidden;
      min-height: 160px;
      /* Opacity here is to prevent content from showing after overflow being hidden on IE */
      opacity: 0.99;
      z-index: 2;
      filter: blur(0px);

      &.reloading {
        filter: blur(14px);
        opacity: 0.5;
      }

      .pinch-zoom {
        img {
          text-indent: 100%;
          white-space: nowrap;
          overflow: hidden;
          cursor: zoom-in;

          &.move-cursor,
          &.zoomImg {
            cursor: zoom-out;
          }
        }
      }

      .swiper-pagination {
        visibility: hidden;
        opacity: 0;
        transition: opacity 500ms ease-out, visibility 0ms ease 500ms;

        &.show {
          opacity: 1;
          visibility: visible;
          transition: opacity 500ms ease-in;
        }
      }

      .swiper-wrapper {
        .swiper-slide-duplicate {
          img {
            opacity: 1;
          }
        }
      }

      /* purgecss ignore */
      &:not(.has-pinch) {
        .preserve-ratio-container {
          display: flex;
          align-items: center;
        }
      }
    }
    .swiper-lazy-preloader {
      top: 50px;
    }
  }

  .swiper-pagination-desktop {
    display: none;
  }

  /* purgecss ignore */
  .swiper-pagination-bullet-active {
    background: var(--coral) !important;
  }

  .swiper-pagination-mobile {
    text-align: center;
    margin-top: var(--space-2);
    margin-bottom: var(--space-2);
  }

  @media (min-width: $tablet-breakpoint) {
    @define-mixin playerHide {
      transition: opacity 0.5s ease, visibility 0s ease 0s;

      &.player-hide {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.5s ease, visibility 0s ease 0.5s;
      }
    }

    margin-bottom: 20px;
    .swiper-pagination-desktop {
      position: absolute;
      bottom: 10px;
      left: 20px;
      z-index: 2;
      display: block;
      padding: 0px;
      visibility: visible;
      opacity: 1;
      @mixin playerHide;

      .item {
        display: inline-block;
        position: relative;
        margin-right: 4px;
        border-radius: var(--circle-border-radius);
        border: 3px solid var(--transparent);
        box-shadow: 0 0 0 1px var(--transparent);

        .thumbnail-link {
          width: 28px;
          height: 28px;
          display: block;
          overflow: hidden;
          border-radius: var(--circle-border-radius);
          -webkit-mask-image: -webkit-radial-gradient(white, black);
        }

        &.active {
          mask-image: unset;
          border: 3px solid var(--white);
          box-shadow: 0 0 0 1px var(--coral);
        }

        img,
        svg {
          max-width: 64px;
          width: auto;
          margin-left: -12px;
        }

        a {
          &::before {
            content: ' ';
            display: inline-block;
            vertical-align: middle;
            height: 28px;
            width: 28px;
            position: absolute;
            border-radius: 18px;
            background: rgba(0, 0, 0, 0.09);
          }

          .icon-thumbnail-play,
          .icon-thumbnail-pause {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            margin: auto;
            width: 100%;
          }
        }
      }
    }
    .swiper-pagination-mobile {
      display: none;
    }
    .activeImage {
      .pinch-zoom-container {
        min-height: 300px;
      }
      .swiper-container {
        min-height: 300px;
      }
      .swiper-lazy-preloader {
        top: 250px;
      }
    }
  }
}
</style>
